import React, { useEffect, useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import { Box, Button, Grid, IconButton, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Loading from "../../../../common/loader/loading";

const timelines = ["daily", "weekly", "monthly"];
const shiftTypes = ["Day Shift", "Night Shift", "Sleep In"];

const TaskForm = ({ initialValues, onSubmit, onClose }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [deputyManagers, setDeputyManagers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Make the API request
        const response = await axios.get("/api/auth/users");
        // Filter users with roleid === 4 and update state
        const filteredUsers = response.data.filter((user) => user.roleid === 4);
        setUsers(filteredUsers);
        const filteredManagers = response.data.filter((user) => user.roleid === 2);
        setDeputyManagers(filteredManagers);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => {
        const errors = {};
        if (!values.taskName) {
          errors.taskName = "Task Name is required";
        }
        if (!values.description) {
          errors.description = "Description is required";
        }

        if (!values.shiftType) {
          errors.shiftType = "Shift Type is required";
        }
        if (!values.timeline) {
          errors.timeline = "Timeline is required";
        }

        return errors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        const payload = {
          ...values,
          checklist: values.checklist.map((subtask) => ({
            item: subtask.item,
            status: subtask.status.toString(),
          })),
        };

        onSubmit(payload)
          .then(() => {
            navigate("/manager-task-list");
          })
          .catch((error) => {})
          .finally(() => {
            setSubmitting(false);
          });
      }}
    >
      {({ values, handleChange, handleBlur, setFieldValue, isValid, dirty }) => (
        <Form id='taskForm'>
          <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
            Task Name
          </Typography>
          <TextField
            name='taskName'
            fullWidth
            variant='outlined'
            placeholder='Enter task title'
            value={values.taskName}
            onChange={handleChange}
            onBlur={handleBlur}
            sx={{ mt: 1 }}
          />

          {/* Checklist */}
          {values.checklist.length > 0 && (
            <Typography sx={{ fontSize: "14px", color: "#1364FF", mt: 2, ml: 2 }}>SubTasks</Typography>
          )}
          <FieldArray
            name='checklist'
            render={(arrayHelpers) => (
              <Box ml={2}>
                {values.checklist.map((subtask, index) => (
                  <Grid container spacing={2} alignItems='center' sx={{ mt: 1 }} key={index}>
                    <Grid item xs={10}>
                      <TextField
                        name={`checklist.${index}.item`}
                        fullWidth
                        variant='outlined'
                        placeholder='Enter subtask'
                        value={subtask.item}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <IconButton color='info' onClick={() => arrayHelpers.remove(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  sx={{ mt: 1, textTransform: "none" }}
                  onClick={() => arrayHelpers.push({ item: "", status: false })}
                >
                  Add A Subtask related to main task
                </Button>
              </Box>
            )}
          />

          {/* Description */}

          <Typography sx={{ fontSize: "14px", color: "black", mt: 2 }}>Description</Typography>
          <TextField
            name='description'
            fullWidth
            variant='outlined'
            multiline
            placeholder='Enter task description'
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            sx={{ mt: 1 }}
          />

          {/* Additional Fields */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Assignee */}
            <Grid item xs={12} lg={4}>
              <Autocomplete
                id='assignee'
                options={users.map((item) => item?.username)}
                value={values.assignee}
                onChange={(event, newValue) => setFieldValue("assignee", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                      Assignee
                    </Typography>
                    <TextField {...params} placeholder='Select' />
                  </>
                )}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <Autocomplete
                id='shiftType'
                options={shiftTypes}
                value={values.shiftType}
                onChange={(event, newValue) => setFieldValue("shiftType", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                      Shift Type
                    </Typography>
                    <TextField {...params} placeholder='Select' />
                  </>
                )}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <Autocomplete
                id='timeline'
                options={timelines}
                value={values.timeline}
                onChange={(event, newValue) => setFieldValue("timeline", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                      Timeline
                    </Typography>
                    <TextField {...params} placeholder='Select' />
                  </>
                )}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* managed By */}
            <Grid item xs={12} lg={4}>
              <Autocomplete
                id='managed_by'
                options={deputyManagers.map((item) => item?.username)}
                value={values.managed_by}
                onChange={(event, newValue) => setFieldValue("managed_by", newValue)}
                renderInput={(params) => (
                  <>
                    <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                      Managed BY
                    </Typography>
                    <TextField {...params} placeholder='Select' />
                  </>
                )}
                fullWidth
              />
            </Grid>
          </Grid>
          {/* Submit button */}
          <Grid item xs={12} mt={2}>
            <Grid container spacing={1} className='submit-button-container'>
              <Grid item>
                <Button
                  onClick={() => onClose()}
                  variant='contained'
                  color='primary'
                  className='submit-button'
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  className='submit-button'
                  sx={{ textTransform: "none" }}
                  disabled={!(isValid && dirty)} // Disable button when form is invalid or unchanged
                >
                  Schedule
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default TaskForm;
